import { useCallback } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Typography } from '@mui/material'

import { HtmlTemplateEditorField } from '@components/HtmlTemplateEditorField/HtmlTemplateEditorField'

import { PrintFormFormValues } from '../types'

type PartialFieldProps = { partialName: string }

const PartialTemplateFieldRow = ({ partialName }: PartialFieldProps) => {
  const { t } = useTranslation()
  const { control, setValue, getValues } = useFormContext<PrintFormFormValues>()

  const documentPartials = useWatch({ control, name: 'documentPartials' }) ?? {}
  const scriptValue = documentPartials[partialName] ?? ''

  const handleSaveScript = useCallback(
    (value: string) => {
      const current = getValues('documentPartials') ?? {}

      setValue('documentPartials', { ...current, [partialName]: value }, { shouldDirty: true })
    },
    [getValues, partialName, setValue]
  )

  return (
    <HtmlTemplateEditorField
      buttonLabel={t('printForms.templateEditor.editPartial', { name: partialName })}
      dialogTitle={t('printForms.templateEditor.partialDialogTitle', { name: partialName })}
      scriptValue={scriptValue}
      onSaveScript={handleSaveScript}
    />
  )
}

export const PrintFormTemplateEditors = () => {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext<PrintFormFormValues>()

  const documentTemplate = useWatch({ control, name: 'documentTemplate' }) ?? ''
  const documentPartials = useWatch({ control, name: 'documentPartials' }) ?? {}

  const partialNames = Object.keys(documentPartials).sort()

  const handleSaveMainTemplate = useCallback(
    (value: string) => setValue('documentTemplate', value, { shouldDirty: true }),
    [setValue]
  )

  return (
    <>
      <HtmlTemplateEditorField
        buttonLabel={t('printForms.templateEditor.editMainTemplate')}
        dialogTitle={t('printForms.templateEditor.mainDialogTitle')}
        scriptValue={documentTemplate}
        onSaveScript={handleSaveMainTemplate}
      />

      {partialNames.length > 0 && (
        <>
          <Typography mb={1} mt={2} variant='subtitle1'>
            {t('printForms.templateEditor.partialsTitle')}
          </Typography>
          {partialNames.map(name => (
            <PartialTemplateFieldRow key={name} partialName={name} />
          ))}
        </>
      )}
    </>
  )
}
